import type { ProcessDraftStep } from '@bliks-io/contracts';
import { getSwimlaneInfo, resolveStepSwimlane, type AssigneeLookup } from '@bliks-io/ui';
import { draftStepToDisplay } from '../adapters/draftStepToDisplay';

/**
 * Mirror the builder's two-pass swimlane resolution to pick the lane key that
 * a freshly-created step should inherit when forked/inserted/merged near
 * `step`. Returns `undefined` for steps that resolve to `unassigned`, which
 * the edge-actions treat as "no hint to forward".
 *
 * `allSteps` is the current draft's full step list — passed in so this helper
 * stays decoupled from the cache layer (callers already hold the cache).
 *
 * `assigneeLookup` mirrors the one the builder uses on the editor canvas; it's
 * required for `getSwimlaneInfo` to resolve an entity-ref `assignedTo` (the
 * canonical wire shape since the slot-union refactor) back to a populated
 * user/team and from there to a department lane.
 */
export function resolveCurrentLaneKey(
  step: ProcessDraftStep,
  allSteps: ProcessDraftStep[],
  assigneeLookup?: AssigneeLookup
): string | undefined {
  // Pass A: collect every lane that a real assignee contributes — only those
  // can elevate a hint-only step out of `unassigned` in Pass B.
  const knownLanes = new Map<string, ReturnType<typeof getSwimlaneInfo>>();
  for (const candidate of allSteps) {
    if (!candidate.assignedTo || !candidate.assignedToModel) continue;
    const info = getSwimlaneInfo(draftStepToDisplay(candidate, ''), assigneeLookup);
    if (info.laneType !== 'unassigned') knownLanes.set(info.key, info);
  }
  const resolved = resolveStepSwimlane(draftStepToDisplay(step, ''), knownLanes, assigneeLookup);
  return resolved.laneType === 'unassigned' ? undefined : resolved.key;
}
